'use client'

import { Star } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { dayBarProgress } from '../day-bar-progress'
import { RaceAvatar } from './race-avatar'
import { rankRows } from './race-lanes'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

export const DAY_BARS_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const

export interface DayBarsDay {
  /** Local calendar date, YYYY-MM-DD. */
  date: string
  value: number
}

export interface DayBarsRow {
  id: string
  name: string
  picture?: string | null
  /** Monday through Friday, in order. */
  days: DayBarsDay[]
  /** The week's total in the selected unit, shown on the right. */
  value: number
  you?: boolean
  color?: string
}

export interface DayBarsProps extends React.HTMLAttributes<HTMLDivElement> {
  rows: DayBarsRow[]
  /** Today's date, YYYY-MM-DD. Legacy bars get an outline; goal charts mark the weekday. */
  today?: string
  /** Opt in to goal colors, stars and a fixed 0–2× daily-goal scale. Omit for relative bars. */
  dailyGoal?: number | null
  /** Tooltip body for a bar. See `DayBarsTooltipBody` for the layout. */
  tooltip?: (row: DayBarsRow, day: DayBarsDay, dayIndex: number) => React.ReactNode
  formatValue?: (value: number) => string
}

const defaultFormat = (v: number) => v.toLocaleString('en-US')

// Scoped and deduplicated by React, just like the race-line animation styles.
const GOAL_STYLES = `
[data-day-bars-goal-fill] { overflow: hidden; transition: height 200ms ease, background-color 200ms ease; }
[data-day-bar][data-goal-reached=true] [data-day-bars-goal-fill]::after { content: ''; position: absolute; inset: 0; background: linear-gradient(105deg, transparent 20%, color-mix(in srgb, var(--primary-foreground) 40%, transparent) 50%, transparent 80%); transform: translateX(-130%); }
[data-day-bar]:is(:hover,:focus-visible,[data-open=true],[data-celebrating=true]) [data-day-bars-star] { animation: ordo-day-star 700ms cubic-bezier(.2,.8,.25,1) 1 both; animation-delay: calc(var(--day-star-index,0) * 90ms); }
[data-day-bar]:is(:hover,:focus-visible,[data-open=true],[data-celebrating=true]) [data-day-bars-goal-fill]::after { animation: ordo-day-shine 700ms ease-out 1; }
[data-day-bar][data-celebrating=true] [data-day-bars-spark] { animation: ordo-day-spark 850ms ease-out 1 both; }
[data-day-bar][data-celebrating=true] [data-day-bars-halo] { animation: ordo-day-halo 700ms ease-out 1 both; }
@keyframes ordo-day-star { 0% { transform: scale(.7) rotate(-15deg); } 45% { transform: scale(1.25) rotate(8deg); } 75% { transform: scale(.97) rotate(-2deg); } 100% { transform: scale(1) rotate(0); } }
@keyframes ordo-day-shine { from { transform: translateX(-130%); } to { transform: translateX(130%); } }
@keyframes ordo-day-spark { 0% { opacity: 0; transform: translate(0,0) scale(.3); } 15% { opacity: 1; } 100% { opacity: 0; transform: translate(var(--spark-x),var(--spark-y)) rotate(60deg) scale(.2); } }
@keyframes ordo-day-halo { from { opacity: .7; transform: scale(.35); } to { opacity: 0; transform: scale(1.6); } }
@media (prefers-reduced-motion: reduce) {
  [data-day-bars-goal-fill] { transition: none; }
  [data-day-bar] [data-day-bars-star], [data-day-bar] [data-day-bars-spark], [data-day-bar] [data-day-bars-halo], [data-day-bar] [data-day-bars-goal-fill]::after { animation: none !important; }
}`

const SPARKS = [
  [-18, -14],
  [0, -20],
  [18, -14],
  [-21, 3],
  [21, 3],
  [0, 15],
] as const

const DayBar = React.forwardRef<
  HTMLButtonElement,
  {
    row: DayBarsRow
    day: DayBarsDay
    index: number
    today?: string
    dailyGoal?: number | null
    max: number
    isOpen: boolean
    formatValue: (value: number) => string
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(function DayBar({ row, day, index, today, dailyGoal, max, isOpen, formatValue, ...triggerProps }, forwardedRef) {
  const progress = dayBarProgress(day.value, dailyGoal, max)
  const future = progress.goal != null && today != null && day.date > today
  const stars = future ? 0 : progress.stars
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  React.useImperativeHandle(forwardedRef, () => buttonRef.current!)
  const previous = React.useRef({ goal: progress.goal, stars })
  React.useEffect(() => {
    const before = previous.current
    previous.current = { goal: progress.goal, stars }
    const button = buttonRef.current
    if (!button || progress.goal == null || before.goal !== progress.goal || stars <= before.stars) {
      return
    }
    button.removeAttribute('data-celebrating')
    // Restart a finite celebration when a later refresh crosses the next milestone.
    void button.offsetWidth
    button.setAttribute('data-celebrating', 'true')
    const timer = setTimeout(() => button.removeAttribute('data-celebrating'), 1100)
    return () => {
      clearTimeout(timer)
      button.removeAttribute('data-celebrating')
    }
  }, [progress.goal, stars])
  const isToday = today !== undefined && day.date === today
  const label = `${row.you ? 'You' : row.name} · ${DAY_BARS_LABELS[index] ?? day.date} · ${formatValue(day.value)}`
  const goalLabel =
    progress.goal == null
      ? ''
      : future
        ? ' · Upcoming'
        : ` · ${Math.round((day.value / progress.goal) * 100)}% of daily goal${stars ? ` · ${stars} ${stars === 1 ? 'star' : 'stars'}` : ''}${progress.capped ? ' · bar capped at twice the daily goal' : ''}`
  return (
    <button
      ref={buttonRef}
      type="button"
      {...triggerProps}
      data-day-bar
      data-active={day.value > 0 ? 'true' : undefined}
      data-open={isOpen ? 'true' : undefined}
      data-today={isToday ? 'true' : undefined}
      data-goal-reached={stars > 0 ? 'true' : undefined}
      data-star-count={progress.goal != null ? stars : undefined}
      aria-label={label + goalLabel}
      className={cn(
        'block min-h-[3px] w-full rounded-t rounded-b-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        progress.goal != null
          ? 'relative z-10 h-10'
          : day.value > 0
            ? 'bg-primary hover:brightness-125 data-[open=true]:brightness-125'
            : 'bg-border hover:bg-muted-foreground data-[open=true]:bg-muted-foreground',
        progress.goal == null && isToday && 'outline outline-2 outline-offset-1 outline-emerald-500',
      )}
      style={progress.goal == null ? { height: `${progress.height}%` } : undefined}>
      {progress.goal != null && (
        <>
          <span
            data-day-bars-goal-fill
            className={cn(
              'absolute inset-x-0 bottom-0 min-h-[3px] rounded-t rounded-b-sm',
              day.value > 0 && !future ? (isToday ? 'bg-muted-foreground/50' : 'bg-primary') : 'bg-border',
            )}
            style={{ height: `${progress.height}%` }}
          />
          {stars > 0 && (
            <span
              aria-hidden
              data-day-bars-stars
              className="pointer-events-none absolute left-1/2 flex h-[9px] -translate-x-1/2 items-center gap-px leading-none text-amber-400"
              style={{ top: `calc(100% - ${progress.height}% + 3px)` }}>
              {Array.from({ length: stars }, (_, star) => (
                <Star
                  key={star}
                  data-day-bars-star
                  className="block size-[9px] shrink-0 overflow-visible fill-current drop-shadow-sm"
                  style={{ '--day-star-index': star } as React.CSSProperties}
                />
              ))}
            </span>
          )}
          {stars > 0 && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-1/2 text-amber-400"
              style={{ top: `calc(100% - ${progress.height}% + 8px)` }}>
              <span
                data-day-bars-halo
                className="absolute -left-3 -top-3 size-6 rounded-full border border-current opacity-0"
              />
              {SPARKS.map(([x, y], spark) => (
                <span
                  key={spark}
                  data-day-bars-spark
                  className="absolute h-1 w-0.5 rounded-sm bg-current opacity-0"
                  style={{ '--spark-x': `${x}px`, '--spark-y': `${y}px` } as React.CSSProperties}
                />
              ))}
            </span>
          )}
        </>
      )}
    </button>
  )
})

/**
 * One row per person, ranked by weekly total: face, name, five Mon–Fri mini
 * bars, total. By default activity is blue, empty days are gray, and today
 * gets a green outline. A daily goal opts into a fixed 0–2× scale, a goal
 * line, blue completed days, gray today, and gold milestone stars in either.
 * Hover and tap show the exact count, including activity beyond the cap.
 */
export function DayBars({
  rows,
  today,
  dailyGoal,
  tooltip,
  formatValue = defaultFormat,
  className,
  ...props
}: DayBarsProps) {
  const ranked = rankRows(rows)
  const max = Math.max(1, ...rows.flatMap((r) => r.days.map((d) => d.value)))
  const goal = dayBarProgress(0, dailyGoal, max).goal
  // The one bar whose tooltip is showing, keyed `${row.id}:${date}`.
  const [openBar, setOpenBar] = React.useState<string | null>(null)

  return (
    <TooltipProvider delayDuration={150}>
      <div data-day-bars className={cn('flex flex-1 flex-col', className)} {...props}>
        {goal != null && (
          <style href="ordo-day-bars-goals" precedence="default">
            {GOAL_STYLES}
          </style>
        )}
        <ol className="flex flex-1 flex-col justify-evenly">
          {ranked.map((row) => (
            <li
              key={row.id}
              data-day-bars-row
              data-owner={row.you ? 'you' : 'other'}
              className={cn(
                'grid grid-cols-[32px_70px_1fr_36px] items-center gap-2.5 text-sm',
                row.you && 'font-semibold',
              )}>
              <RaceAvatar
                name={row.name}
                src={row.picture}
                color={row.color}
                size={32}
                ring={row.you ? 'you' : undefined}
              />
              <span className="truncate">{row.you ? 'You' : row.name}</span>
              <div className="relative grid h-10 grid-cols-5 items-end gap-1.5">
                {goal != null && (
                  <span
                    aria-hidden
                    data-day-bars-goal
                    className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-muted-foreground/50"
                  />
                )}
                {row.days.map((day, i) => {
                  const key = `${row.id}:${day.date}`
                  const isOpen = openBar === key
                  const bar = (
                    <DayBar
                      row={row}
                      day={day}
                      index={i}
                      today={today}
                      dailyGoal={goal}
                      max={max}
                      isOpen={isOpen}
                      formatValue={formatValue}
                    />
                  )
                  if (!tooltip) {
                    return <React.Fragment key={day.date}>{bar}</React.Fragment>
                  }
                  return (
                    <Tooltip
                      key={day.date}
                      open={isOpen}
                      onOpenChange={(open) => setOpenBar((current) => (open ? key : current === key ? null : current))}>
                      <TooltipTrigger
                        asChild
                        onClick={(event) => {
                          // Radix closes a tooltip when its trigger is clicked, and a
                          // touch never hovers; a tap toggles it instead.
                          event.preventDefault()
                          setOpenBar((current) => (current === key ? null : key))
                        }}>
                        {bar}
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[220px] px-2.5 py-2 text-left font-normal leading-snug">
                        {tooltip(row, day, i)}
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
              <span
                data-day-bars-total
                className={cn('text-right tabular-nums', row.you ? 'text-foreground' : 'text-muted-foreground')}>
                {formatValue(row.value)}
              </span>
            </li>
          ))}
        </ol>
        <div className="mt-1 grid grid-cols-[32px_70px_1fr_36px] gap-2.5">
          <span />
          <span />
          <div className="grid grid-cols-5 gap-1.5 text-center text-[11px] text-muted-foreground/80">
            {DAY_BARS_LABELS.map((d, index) => (
              <span
                key={d}
                className={cn(
                  goal != null &&
                    rows[0]?.days[index]?.date === today &&
                    'text-foreground after:mx-auto after:mt-0.5 after:block after:size-0.5 after:rounded-full after:bg-current',
                )}>
                {d}
              </span>
            ))}
          </div>
          <span />
        </div>
      </div>
    </TooltipProvider>
  )
}

export interface DayBarsTooltipBodyProps {
  /** "Mike · Tue, Oct 13" */
  heading: string
  /** The selected unit, once: "10 schools". */
  primary: string
  /** The other units, below and muted: "16 contacts · 24 emails · 7 calls". */
  others?: string
  /** When present, explain the uncapped count against a daily goal. */
  goal?: number
  value?: number
}

/** The bar tooltip's layout: who and when, the selected unit, the rest muted. */
export function DayBarsTooltipBody({ heading, primary, others, goal, value }: DayBarsTooltipBodyProps) {
  return (
    <div data-day-bars-tooltip>
      <div className="font-semibold">{heading}</div>
      <div>{primary}</div>
      {goal != null && goal > 0 && value != null && (
        <div className="opacity-75">
          {Math.round((value / goal) * 100)}% of daily goal ·{' '}
          {value >= goal ? `${defaultFormat(value - goal)} above goal` : `${defaultFormat(goal - value)} to goal`}
        </div>
      )}
      {others && <div className="opacity-75">{others}</div>}
    </div>
  )
}
